import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Panel,
  Flex,
  Label,
  TextInput,
  Select,
  FormHeading,
  Divider,
  Button,
  Span,
  Toggle
} from '@sopranodesign/pulse-components';

const DRIVERS = [
  {
    value: 'Ubisend\\Pulse\\Plugins\\Mcp\\Drivers\\StdioDriver',
    label: 'Stdio (local process)'
  },
  {
    value: 'Ubisend\\Pulse\\Plugins\\Mcp\\Drivers\\HttpDriver',
    label: 'HTTP (remote server)'
  },
  {
    value: 'Ubisend\\Pulse\\Plugins\\Mcp\\Drivers\\MockDriver',
    label: 'Mock (testing)'
  }
];

const DEFAULT_FORM = {
  name: '',
  handler: DRIVERS[0].value,
  base_url: '',
  timeout_ms: 30000,
  enabled: true,
  credentials: {
    token: ''
  },
  config: {
    command: '',
    args: [],
    env: {}
  }
};

const resolveDriverValue = value => {
  if (!value) {
    return DEFAULT_FORM.handler;
  }

  if (DRIVERS.some(driver => driver.value === value)) {
    return value;
  }

  const loweredValue = String(value).toLowerCase();

  if (loweredValue.includes('stdio')) {
    return DRIVERS[0].value;
  }

  if (loweredValue.includes('http')) {
    return DRIVERS[1].value;
  }

  if (loweredValue.includes('mock')) {
    return DRIVERS[2].value;
  }

  return DEFAULT_FORM.handler;
};

const parseConfig = config => {
  if (!config) {
    return {};
  }

  if (typeof config === 'string') {
    try {
      return JSON.parse(config);
    } catch {
      return {};
    }
  }

  if (typeof config === 'object') {
    return config;
  }

  return {};
};

const normalizeProvider = provider => {
  if (!provider) {
    return null;
  }

  const rawConfig =
    provider.config ??
    provider.driver_config ??
    provider.stdio_config ??
    provider.configuration;
  const parsedConfig = parseConfig(rawConfig);

  return {
    ...provider,
    handler: resolveDriverValue(provider.handler ?? provider.driver),
    base_url: provider.base_url ?? provider.baseUrl ?? '',
    timeout_ms: provider.timeout_ms ?? provider.timeout ?? DEFAULT_FORM.timeout_ms,
    enabled:
      typeof provider.enabled === 'boolean'
        ? provider.enabled
        : DEFAULT_FORM.enabled,
    // credentials are encrypted and never returned by the API;
    // always start blank so users must re-enter to change
    credentials: { token: '' },
    config: {
      ...DEFAULT_FORM.config,
      ...parsedConfig,
      args: Array.isArray(parsedConfig?.args) ? parsedConfig.args : [],
      env:
        parsedConfig?.env && typeof parsedConfig.env === 'object'
          ? parsedConfig.env
          : {}
    }
  };
};

const McpProviderDetails = ({ handleSubmit, loading, initialProvider }) => {
  const normalizedProvider = normalizeProvider(initialProvider);

  const [form, setForm] = useState(() => ({
    ...DEFAULT_FORM,
    ...normalizedProvider,
    config: {
      ...DEFAULT_FORM.config,
      ...(normalizedProvider?.config ?? {})
    },
    // always blank — credentials are never returned by the API
    credentials: { token: '' }
  }));

  const [newArg, setNewArg] = useState('');
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvValue, setNewEnvValue] = useState('');

  const isStdio = form.handler === DRIVERS[0].value;
  const isHttp = form.handler === DRIVERS[1].value;

  const valid = Boolean(
    form.name &&
      form.handler &&
      (!isStdio || form.config.command) &&
      (!isHttp || form.base_url) &&
      form.timeout_ms >= 1000 &&
      form.timeout_ms <= 30000
  );

  const onSubmit = event => {
    event.preventDefault();
    if (valid && !loading) {
      const payload = { ...form };
      // don't send credentials if the token field was left blank;
      // the backend preserves the existing encrypted value in that case
      if (!payload.credentials?.token) {
        delete payload.credentials;
      }
      // config is only used by the Stdio driver; omit it for HTTP/Mock
      // so the backend's required_with:config validation does not fire
      if (!isStdio) {
        delete payload.config;
      }
      handleSubmit(payload);
    }
  };

  const setField = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const setConfig = (key, value) =>
    setForm(prev => ({ ...prev, config: { ...prev.config, [key]: value } }));

  const setCredential = (key, value) =>
    setForm(prev => ({ ...prev, credentials: { ...prev.credentials, [key]: value } }));

  const handleAddArg = () => {
    if (!newArg.trim()) {return;}
    setConfig('args', [...(form.config.args ?? []), newArg.trim()]);
    setNewArg('');
  };

  const handleRemoveArg = index =>
    setConfig(
      'args',
      form.config.args.filter((_, i) => i !== index)
    );

  const handleAddEnv = () => {
    if (!newEnvKey.trim()) {return;}
    setConfig('env', { ...form.config.env, [newEnvKey.trim()]: newEnvValue });
    setNewEnvKey('');
    setNewEnvValue('');
  };

  const handleRemoveEnv = key => {
    const updated = { ...form.config.env };
    delete updated[key];
    setConfig('env', updated);
  };

  return (
    <form onSubmit={onSubmit}>
      <Panel>
        <Flex col>
          <FormHeading>General</FormHeading>
          <Flex xSpace>
            <Flex col fat mb>
              <Label htmlFor="provider_name">Name</Label>
              <TextInput
                id="provider_name"
                placeholder="My filesystem server"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
              />
            </Flex>
            <Flex col fat mb>
              <Label htmlFor="provider_driver">Driver</Label>
              <Select
                id="provider_driver"
                options={DRIVERS}
                value={DRIVERS.find(d => d.value === form.handler)}
                onChange={opt => setField('handler', opt.value)}
              />
            </Flex>
          </Flex>

          <Flex xSpace>
            <Flex col fat mb>
              <Label htmlFor="provider_timeout">Timeout (ms)</Label>
              <TextInput
                id="provider_timeout"
                type="number"
                placeholder="30000"
                min={1000}
                max={30000}
                value={form.timeout_ms}
                onChange={e => setField('timeout_ms', Number(e.target.value))}
              />
            </Flex>
            <Flex col fat mb center>
              <Label>Enabled</Label>
              <Toggle
                on={form.enabled}
                handleToggleChange={() => setField('enabled', !form.enabled)}
                title="provider-enabled"
              />
            </Flex>
          </Flex>

          {isHttp && (
            <>
              <Divider fullWidth />
              <FormHeading>HTTP configuration</FormHeading>
              <Flex col fat mb>
                <Label htmlFor="provider_base_url">Base URL</Label>
                <TextInput
                  id="provider_base_url"
                  placeholder="https://mcp.example.com"
                  value={form.base_url}
                  onChange={e => setField('base_url', e.target.value)}
                />
              </Flex>
              <Flex col fat mb>
                <Label htmlFor="provider_token">Bearer Token</Label>
                <TextInput
                  id="provider_token"
                  type="password"
                  placeholder="Leave blank to keep existing token"
                  value={form.credentials.token}
                  onChange={e => setCredential('token', e.target.value)}
                />
              </Flex>
            </>
          )}

          {isStdio && (
            <>
              <Divider fullWidth />
              <FormHeading>Stdio configuration</FormHeading>
              <Flex col fat mb>
                <Label htmlFor="provider_command">Command</Label>
                <TextInput
                  id="provider_command"
                  placeholder="npx"
                  value={form.config.command}
                  onChange={e => setConfig('command', e.target.value)}
                />
              </Flex>

              <Flex col fat mb>
                <Label>Arguments</Label>
                {(form.config.args ?? []).length > 0 && (
                  <Flex fat mb style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    {(form.config.args ?? []).map((arg, i) => (
                      <Flex
                        key={i}
                        center
                        xSpaceSm
                        style={{
                          background: '#f3f4f6',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          padding: '0.25rem 0.5rem 0.25rem 0.75rem',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem'
                        }}>
                        <Span>{arg}</Span>
                        <Button
                          icon="x"
                          variant="inline"
                          aria-label="Remove argument"
                          onClick={() => handleRemoveArg(i)}
                        />
                      </Flex>
                    ))}
                  </Flex>
                )}
                <Flex center xSpace>
                  <Flex fat>
                    <TextInput
                      placeholder="e.g. -y"
                      value={newArg}
                      onChange={e => setNewArg(e.target.value)}
                    />
                  </Flex>
                  <Button icon="plus" onClick={handleAddArg} type="button">
                    Add
                  </Button>
                </Flex>
              </Flex>

              <Flex col fat mb>
                <Label>Environment variables</Label>
                {Object.entries(form.config.env ?? {}).length > 0 && (
                  <Flex col mb style={{ gap: '0.5rem' }}>
                    {Object.entries(form.config.env ?? {}).map(([key, val]) => (
                      <Flex
                        key={key}
                        between
                        center
                        style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          padding: '0.5rem 0.75rem'
                        }}>
                        <Span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          <strong>{key}</strong>
                          {' = '}
                          {val || <em style={{ color: '#9ca3af' }}>empty</em>}
                        </Span>
                        <Button
                          icon="trash"
                          colour="danger"
                          aria-label="Remove env var"
                          onClick={() => handleRemoveEnv(key)}
                        />
                      </Flex>
                    ))}
                  </Flex>
                )}
                <Flex center xSpace>
                  <TextInput
                    placeholder="KEY"
                    value={newEnvKey}
                    onChange={e => setNewEnvKey(e.target.value)}
                  />
                  <Span>=</Span>
                  <Flex fat>
                    <TextInput
                      placeholder="value"
                      value={newEnvValue}
                      onChange={e => setNewEnvValue(e.target.value)}
                    />
                  </Flex>
                  <Button icon="plus" onClick={handleAddEnv} type="button">
                    Add
                  </Button>
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
      </Panel>

      <Flex right mt>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={!valid || loading}>
          Save
        </Button>
      </Flex>
    </form>
  );
};

McpProviderDetails.defaultProps = {
  initialProvider: null
};

McpProviderDetails.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  initialProvider: PropTypes.object
};

export default McpProviderDetails;
